import React, { useState, useEffect, useCallback } from 'react';
import { 
  History, 
  RefreshCw,
  Calendar,
  CalendarDays,
  User,
  ChevronLeft,
  ChevronRight,
  Scale,
  Camera,
  Syringe,
  FlaskConical,
  CreditCard,
  MessageSquare,
  Image,
  Stethoscope,
  FileText,
  Pill,
  ChevronDown,
  ChevronUp,
  Mic,
  Upload,
  FileAudio,
  ClipboardCheck
} from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

interface PetAuditLogEntry {
  id: string;
  pet_id: string;
  practice_id: string;
  event_type: string;
  actor_id: string | null;
  actor_name: string | null;
  pet_owner_id: string | null;
  old_value: Record<string, any> | null;
  new_value: Record<string, any> | null;
  event_metadata: Record<string, any> | null;
  description: string | null;
  created_at: string;
}

interface PetAuditLogResponse {
  audit_logs: PetAuditLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

interface PetAuditLogProps {
  petId: string | undefined;
}

const PetAuditLog: React.FC<PetAuditLogProps> = ({ petId }) => {
  const [logs, setLogs] = useState<PetAuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const pageSize = 25;

  const fetchAuditLogs = useCallback(async () => {
    if (!petId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const offset = (page - 1) * pageSize;
      
      const response = await fetch(
        `${API_ENDPOINTS.PETS.AUDIT(petId)}?limit=${pageSize}&offset=${offset}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }
      
      const data: PetAuditLogResponse = await response.json();
      setLogs(data.audit_logs);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [petId, page]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const totalPages = Math.ceil(total / pageSize);

  const toggleRowExpanded = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      // Pet profile events
      case 'weight_changed':
      case 'pet_created':
      case 'pet_updated':
        return <Scale className="w-4 h-4" />;
      case 'photo_uploaded':
        return <Camera className="w-4 h-4" />;
      
      // Vaccine/Immunization events
      case 'vaccine_ordered':
      case 'vaccine_administered':
      case 'vaccine_scheduled':
      case 'vaccine_updated':
      case 'vaccine_deleted':
      case 'vaccine_cancelled':
        return <Syringe className="w-4 h-4" />;
      
      // Lab events
      case 'lab_ordered':
      case 'lab_status_changed':
      case 'lab_results_received':
      case 'lab_updated':
      case 'lab_deleted':
      case 'lab_attachment_added':
      case 'lab_attachment_deleted':
        return <FlaskConical className="w-4 h-4" />;
      
      // Billing events
      case 'invoice_created':
      case 'invoice_status_changed':
      case 'payment_received':
      case 'order_added':
      case 'order_updated':
      case 'order_deleted':
      case 'credit_updated':
        return <CreditCard className="w-4 h-4" />;
      
      // Communication events
      case 'sms_sent':
      case 'sms_received':
        return <MessageSquare className="w-4 h-4" />;
      case 'mms_sent':
      case 'mms_received':
        return <Image className="w-4 h-4" />;
      
      // Encounter events
      case 'encounter_created':
      case 'encounter_updated':
      case 'encounter_signed':
      case 'encounter_completed':
        return <Stethoscope className="w-4 h-4" />;
      
      // Appointment events
      case 'appointment_created':
      case 'appointment_updated':
      case 'appointment_cancelled':
      case 'appointment_completed':
        return <CalendarDays className="w-4 h-4" />;
      
      // Medical record events
      case 'medical_record_created':
      case 'medical_record_updated':
      case 'medical_record_deleted':
        return <FileText className="w-4 h-4" />;
      
      // Medication events
      case 'medication_prescribed':
      case 'medication_status_changed':
      case 'medication_updated':
      case 'medication_deleted':
      case 'medication_discontinued':
        return <Pill className="w-4 h-4" />;
      
      // Audio recording events
      case 'audio_upload_initiated':
        return <Mic className="w-4 h-4" />;
      case 'audio_upload_completed':
        return <Upload className="w-4 h-4" />;
      case 'audio_transcription_completed':
      case 'audio_soap_extracted':
        return <FileAudio className="w-4 h-4" />;
      
      // Form events
      case 'form_submitted':
        return <ClipboardCheck className="w-4 h-4" />;
      
      default:
        return <History className="w-4 h-4" />;
    }
  };

  const getEventBadgeStyle = (eventType: string) => {
    // Group by category with consistent colors
    if (eventType.includes('weight') || eventType.includes('photo') || eventType.includes('pet_')) {
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    }
    if (eventType.includes('vaccine') || eventType.includes('immunization')) {
      return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400';
    }
    if (eventType.includes('lab')) {
      return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
    }
    if (eventType.includes('invoice') || eventType.includes('payment') || eventType.includes('order') || eventType.includes('credit')) {
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    }
    if (eventType.includes('sms') || eventType.includes('mms')) {
      return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
    }
    if (eventType.includes('encounter')) {
      return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400';
    }
    if (eventType.includes('appointment')) {
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    }
    if (eventType.includes('medical_record')) {
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
    }
    if (eventType.includes('medication')) {
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    }
    if (eventType.includes('audio')) {
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    }
    if (eventType.includes('form')) {
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
    }
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  };

  const formatEventType = (eventType: string) => {
    return eventType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const hasDetails = (log: PetAuditLogEntry) => {
    return (log.old_value && Object.keys(log.old_value).length > 0) ||
           (log.new_value && Object.keys(log.new_value).length > 0);
  };

  if (!petId) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-dark-text-muted">
        <p>No pet selected</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-dark-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
              <History className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                Activity History
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted">
                {total} {total === 1 ? 'event' : 'events'} recorded
              </p>
            </div>
          </div>
          <button
            onClick={fetchAuditLogs}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white dark:bg-dark-bg-tertiary border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading && logs.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 text-red-500">
            <p className="text-lg font-medium">Error loading audit logs</p>
            <p className="text-sm">{error}</p>
            <button
              onClick={fetchAuditLogs}
              className="mt-4 px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-dark-text-muted">
            <History className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-lg font-medium">No activity recorded</p>
            <p className="text-sm">Changes to this pet will appear here</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-dark-bg-tertiary sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Event
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Description
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  By
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider w-10">
                  
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border">
              {logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr 
                    className={`hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors ${
                      hasDetails(log) ? 'cursor-pointer' : ''
                    }`}
                    onClick={() => hasDetails(log) && toggleRowExpanded(log.id)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getEventBadgeStyle(log.event_type)}`}>
                          {getEventIcon(log.event_type)}
                          {formatEventType(log.event_type)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 dark:text-dark-text-primary max-w-md truncate" title={log.description || ''}>
                        {log.description || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {log.actor_name ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                            <User className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                          </div>
                          <span className="text-sm text-gray-700 dark:text-dark-text-secondary">
                            {log.actor_name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">System</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-dark-text-secondary" title={formatDate(log.created_at)}>
                        <Calendar className="w-3 h-3" />
                        {formatRelativeTime(log.created_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {hasDetails(log) && (
                        <button className="p-1 hover:bg-gray-100 dark:hover:bg-dark-bg-hover rounded">
                          {expandedRows.has(log.id) ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                  {/* Expanded Row Details */}
                  {expandedRows.has(log.id) && hasDetails(log) && (
                    <tr className="bg-gray-50 dark:bg-dark-bg-tertiary">
                      <td colSpan={5} className="px-4 py-3">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          {log.old_value && Object.keys(log.old_value).length > 0 && (
                            <div>
                              <div className="font-medium text-gray-500 dark:text-dark-text-muted mb-1">Previous Value</div>
                              <div className="bg-white dark:bg-dark-bg-secondary rounded p-2 border border-gray-200 dark:border-dark-border">
                                <pre className="text-xs text-gray-700 dark:text-dark-text-secondary whitespace-pre-wrap">
                                  {JSON.stringify(log.old_value, null, 2)}
                                </pre>
                              </div>
                            </div>
                          )}
                          {log.new_value && Object.keys(log.new_value).length > 0 && (
                            <div>
                              <div className="font-medium text-gray-500 dark:text-dark-text-muted mb-1">New Value</div>
                              <div className="bg-white dark:bg-dark-bg-secondary rounded p-2 border border-gray-200 dark:border-dark-border">
                                <pre className="text-xs text-gray-700 dark:text-dark-text-secondary whitespace-pre-wrap">
                                  {JSON.stringify(log.new_value, null, 2)}
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} of {total} entries
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="px-3 py-1.5 text-sm text-gray-700 dark:text-dark-text-secondary">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PetAuditLog;
